import { useCallback, useEffect, useState } from 'react';
import {
  buildLoadExecutionPayload,
  fetchLoadExecutionScorecards,
  LoadExecutionScorecardsData,
  AppliedFiltersForLoadExecution,
} from '../services/onTimeDispatchLoadExecutionApi';

export interface UseLoadExecutionScorecardsReturn {
  scorecardData: LoadExecutionScorecardsData | null;
  scorecardLoading: boolean;
  scorecardError: string | null;
  triggerScorecardsRefresh: () => void;
  resetScorecardsData: () => void;
}

/**
 * Hook to fetch Load Execution scorecards based on applied filters
 * Mirrors the pattern of useOnTimeDispatchKpi for consistency
 *
 * @param appliedFilters Current applied filters snapshot
 * @param isActive Whether the Load Execution tab is active
 * @param adminLocations Full list of available locations for ID resolution
 * @returns Hook interface with data, loading, error, and control functions
 */
export function useLoadExecutionScorecards(
  appliedFilters: AppliedFiltersForLoadExecution,
  isActive: boolean,
  adminLocations: any[]
): UseLoadExecutionScorecardsReturn {
  const [scorecardData, setScorecardData] = useState<LoadExecutionScorecardsData | null>(null);
  const [scorecardLoading, setScorecardLoading] = useState(false);
  const [scorecardError, setScorecardError] = useState<string | null>(null);
  const [triggerCount, setTriggerCount] = useState(0);

  const resetScorecardsData = useCallback(() => {
    console.log('[Load Execution Hook] Resetting scorecard data');
    setScorecardData(null);
    setScorecardError(null);
  }, []);

  const triggerScorecardsRefresh = useCallback(() => {
    console.log('[Load Execution Hook] Triggering manual refresh');
    setTriggerCount((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!isActive) {
      console.log('[Load Execution Hook] Tab not active, skipping fetch');
      return;
    }

    // ✅ FIX: Data readiness check - only proceed when adminLocations is fully loaded
    // This prevents race condition where API call happens before location data is available
    // Result: Initial API call will use ALL applicable location IDs instead of falling back to empty/single
    if (!adminLocations || adminLocations.length === 0) {
      console.log('[Load Execution Hook] Waiting for adminLocations to load (current length:', adminLocations?.length || 0, ')');
      return;
    }

    let isMounted = true;

    const performFetch = async () => {
      try {
        setScorecardLoading(true);
        setScorecardError(null);

        // Build the request payload with ALL applicable location IDs
        const request = buildLoadExecutionPayload(appliedFilters, adminLocations);
        console.log(
          '[Load Execution Hook] Fetching with request:',
          JSON.stringify(request, null, 2)
        );

        // ✅ Verify that locationId array contains all expected locations
        if (request.locationId) {
          console.log(
            '[Load Execution Hook] Request includes',
            request.locationId.length,
            'location IDs:',
            request.locationId
          );
        }

        // Call the API
        const data = await fetchLoadExecutionScorecards(request);

        if (!isMounted) return;

        setScorecardData(data);
        setScorecardLoading(false);
        console.log('[Load Execution Hook] Scorecard data loaded successfully:', data);
      } catch (err) {
        if (!isMounted) return;

        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch scorecard data';
        setScorecardError(errorMessage);
        setScorecardLoading(false);
        console.error('[Load Execution Hook] Scorecard fetch failed:', errorMessage);
      }
    };

    // Debounce the fetch slightly to avoid multiple rapid requests
    const debounceTimer = setTimeout(() => {
      performFetch();
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(debounceTimer);
    };
  }, [appliedFilters, isActive, triggerCount, adminLocations]);

  return {
    scorecardData,
    scorecardLoading,
    scorecardError,
    triggerScorecardsRefresh,
    resetScorecardsData,
  };
}
